// [S-802] The variables a template carries, wherever it is shown.
//
// Which set to draw, in order: the APPROVED one, because that is what a draft
// will actually be filled from; failing that the reviewer's own proposal, minus
// anything they removed, because a variable they have accepted but not yet
// approved is still the answer to "which of these gaps does a deal fill?".
import { useMemo } from "react";
import type { CertifiedTemplateRow } from "../../data/templatesQuery";
import {
  useTemplateVariableProposal,
  useTemplateVariablesApproved,
} from "../../data/templateVariablesQuery";
import { withVariableMarks, type HighlightSlot } from "./variableHighlight";
import { withMarkerBoundaries } from "./variableMarkers";

export type VariableMarks = {
  /** The slots to draw. Empty until a set loads, or when none exists. */
  slots: HighlightSlot[];
  /** Which set they came from — the one a save must write back. */
  source: "approved" | "proposal";
  /** `html` with those slots wrapped in `<span class="tv-mark">` — for the
   *  READ-ONLY views, where nothing can move and markup is the simplest thing
   *  that works. */
  mark: (html: string) => string;
  /** `html` with each slot's boundaries, for an EDITOR. The editor turns these
   *  into markers it transforms through every edit (see variableMarkers.ts);
   *  nothing of the marking is ever content, and the save reads the boundaries
   *  back to re-anchor the set. */
  markForEditing: (html: string) => string;
};

export function useVariableMarks(template: CertifiedTemplateRow): VariableMarks {
  const ref = {
    templateId: template.id,
    tenantId: template.layerSource === "L1" ? null : template.tenantId,
  };
  const approved = useTemplateVariablesApproved(ref);
  const proposal = useTemplateVariableProposal(ref, null);

  const drawn = useMemo<{ slots: HighlightSlot[]; source: "approved" | "proposal" }>(() => {
    const fromApproved = approved.data?.approved?.slots;
    if (fromApproved !== undefined && fromApproved.length > 0) {
      return { slots: fromApproved, source: "approved" };
    }
    const fromProposal = proposal.data?.proposal?.slots ?? [];
    return { slots: fromProposal.filter((s) => s.reviewed !== false), source: "proposal" };
  }, [approved.data, proposal.data]);

  return useMemo(
    () => ({
      slots: drawn.slots,
      source: drawn.source,
      mark: (html: string) =>
        drawn.slots.length === 0 ? html : withVariableMarks(html, drawn.slots),
      markForEditing: (html: string) =>
        drawn.slots.length === 0 ? html : withMarkerBoundaries(html, drawn.slots).html,
    }),
    [drawn]
  );
}
